import React, { useState, useCallback, useRef } from 'react';
import { X, ChevronDown, ChevronUp, RefreshCw, RotateCw, ChevronLeft, ChevronRight, Code } from 'lucide-react';

interface Chord {
    note: string;
    quality: 'major' | 'minor';
    position?: number;
}

interface BuildProgressionModalProps {
    isOpen: boolean;
    onClose: () => void;
    initialChords?: string;
    onSave: (chords: string) => void;
}

interface HistoryEntry {
    chords: Chord[];
    key: string | null;
    keyIsMinor: boolean;
    cursor: number;
    barlines: number[];
}

const NOTES = ['C', 'C#', 'D', 'Eb', 'E', 'F', 'F#', 'G', 'Ab', 'A', 'Bb', 'B'];
export const BuildProgressionModal: React.FC<BuildProgressionModalProps> = ({
    isOpen,
    onClose,
    initialChords = '',
    onSave
}) => {
    const [chords, setChords] = useState<Chord[]>([]);
    const [cursorIndex, setCursorIndex] = useState(0);
    const [mode, setMode] = useState<'major' | 'minor'>('major');
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [historyIndex, setHistoryIndex] = useState(-1);
    const [keySignature, setKeySignature] = useState<string | null>(null);
    const [keyMode, setKeyMode] = useState<'major' | 'minor'>('major');
    const [barlines, setBarlines] = useState<number[]>([]);
    const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
    const currentAudioRef = useRef<HTMLAudioElement | null>(null);

    const parseProgression = useCallback((chordString: string): { chords: Chord[]; barlines: number[] } => {
        if (!chordString.trim()) return { chords: [], barlines: [] };

        let segments = chordString.split('|');
        if (segments.length > 0 && segments[0].includes(':')) {
            const colonIndex = segments[0].indexOf(':');
            const afterColon = segments[0].slice(colonIndex + 1).trim();
            if (afterColon) {
                segments[0] = afterColon;
            } else {
                segments = segments.slice(1);
            }
        }

        const parsedChords: Chord[] = [];
        const barlinePositions: number[] = [];

        segments.forEach((segment, index) => {
            const tokens = segment
                .split(/[\s,]+/)
                .map(token => token.trim())
                .filter(Boolean);

            tokens.forEach(token => {
                const cleanPart = token.replace(/^[^A-G]*/, '').trim();
                if (!cleanPart) return;
                const noteMatch = cleanPart.match(/^([A-G][#b]?)/);
                const note = noteMatch ? noteMatch[1] : cleanPart;
                const isMinor = cleanPart.toLowerCase().includes('m') || cleanPart.toLowerCase().includes('min');
                const parsedNote = note.replace(/[^A-G#b]/g, '');
                if (!parsedNote) return;
                parsedChords.push({
                    note: parsedNote,
                    quality: (isMinor ? 'minor' : 'major')
                });
            });

            if (index < segments.length - 1) {
                barlinePositions.push(parsedChords.length);
            }
        });

        const normalizedBarlines = Array.from(new Set(barlinePositions))
            .filter(pos => pos > 0 && pos <= parsedChords.length)
            .sort((a, b) => a - b);

        return { chords: parsedChords, barlines: normalizedBarlines };
    }, []);

    const formatChords = useCallback((chords: Chord[], barlines: number[], key?: string | null, keyIsMinor?: boolean): string => {
        if (chords.length === 0) {
            if (key) {
                return keyIsMinor ? `${key}m:` : `${key}:`;
            }
            return '';
        }

        const barlineSet = new Set(barlines.filter(pos => pos > 0 && pos <= chords.length));
        const measures: string[] = [];
        let buffer: string[] = [];

        chords.forEach((chord, index) => {
            const note = chord.note;
            const quality = chord.quality === 'minor' ? 'm' : '';
            buffer.push(`${note}${quality}`);

            if (barlineSet.has(index + 1)) {
                measures.push(buffer.join(' '));
                buffer = [];
            }
        });

        if (buffer.length > 0) {
            measures.push(buffer.join(' '));
        }

        const progression = measures.join(' | ');
        if (key) {
            const keyFormatted = keyIsMinor ? `${key}m` : key;
            return `${keyFormatted}: ${progression}`;
        }
        return progression;
    }, []);

    const extractKeySignature = useCallback((chordString: string): { key: string | null; isMinor: boolean; progression: string } => {
        const trimmed = chordString.trim();
        if (!trimmed) return { key: null, isMinor: false, progression: '' };

        const colonIndex = trimmed.indexOf(':');
        if (colonIndex === -1) return { key: null, isMinor: false, progression: trimmed };

        const possibleKey = trimmed.slice(0, colonIndex).trim();
        const progression = trimmed.slice(colonIndex + 1).trim();

        if (!possibleKey) {
            return { key: null, isMinor: false, progression: trimmed };
        }

        const isMinor = /m$/i.test(possibleKey) || /-$/.test(possibleKey);
        const keyWithoutM = possibleKey.replace(/m$/i, '').replace(/-$/, '').trim();
        const isValidKey = /^[A-G][#b]?$/i.test(keyWithoutM);

        return {
            key: isValidKey ? keyWithoutM : null,
            isMinor: isValidKey ? isMinor : false,
            progression: progression || trimmed
        };
    }, []);

    const normalizeBarlines = useCallback((lines: number[], length: number) => {
        return Array.from(new Set(lines.filter(pos => pos > 0 && pos <= length))).sort((a, b) => a - b);
    }, []);

    const shiftBarlinesAfterInsert = useCallback((lines: number[], insertIndex: number, lengthAfterInsert: number) => {
        return normalizeBarlines(
            lines.map(pos => (pos > insertIndex ? pos + 1 : pos)),
            lengthAfterInsert
        );
    }, [normalizeBarlines]);

    const shiftBarlinesAfterRemoval = useCallback((lines: number[], removeIndex: number, lengthAfterRemoval: number) => {
        return normalizeBarlines(
            lines
                .map(pos => (pos > removeIndex ? pos - 1 : pos))
                .filter(pos => pos <= lengthAfterRemoval),
            lengthAfterRemoval
        );
    }, [normalizeBarlines]);

    const resetState = useCallback(() => {
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current.currentTime = 0;
            currentAudioRef.current = null;
        }
        setChords([]);
        setCursorIndex(0);
        setHistory([]);
        setHistoryIndex(-1);
        setKeySignature(null);
        setKeyMode('major');
        setBarlines([]);
    }, []);

    const normalizeKeyToNotesFormat = useCallback((key: string): string | null => {
        if (!key) return null;
        const normalized = key.trim().replace(/m$/i, '');
        const upperKey = normalized.toUpperCase();
        const noteIndex = NOTES.findIndex(note => note.toUpperCase() === upperKey);
        if (noteIndex !== -1) {
            return NOTES[noteIndex];
        }
        return null;
    }, []);

    React.useEffect(() => {
        if (isOpen) {
            const trimmed = initialChords.trim();

            if (trimmed) {
                const { key, isMinor, progression } = extractKeySignature(trimmed);
                const source = progression || trimmed;
                const { chords: parsedChords, barlines: parsedBarlines } = parseProgression(source);

                let finalKey = key;
                let finalIsMinor = isMinor;
                if (!finalKey && parsedChords.length > 0) {
                    const firstChordNote = parsedChords[0].note;
                    finalKey = normalizeKeyToNotesFormat(firstChordNote);
                } else if (finalKey) {
                    finalKey = normalizeKeyToNotesFormat(finalKey) || finalKey;
                }

                if (parsedChords.length > 0) {
                    const defaultCursor = parsedChords.length;
                    setChords(parsedChords);
                    setCursorIndex(defaultCursor);
                    setBarlines(parsedBarlines);
                    setHistory([{ chords: parsedChords, key: finalKey, keyIsMinor: finalIsMinor, cursor: defaultCursor, barlines: parsedBarlines }]);
                    setHistoryIndex(0);
                    setKeySignature(finalKey);
                    setKeyMode(finalIsMinor ? 'minor' : 'major');
                    return;
                } else if (finalKey) {
                    setKeySignature(finalKey);
                    setKeyMode(finalIsMinor ? 'minor' : 'major');
                }
            }

            resetState();
        }
    }, [isOpen, initialChords, parseProgression, resetState, extractKeySignature, normalizeKeyToNotesFormat]);

    const addToHistory = useCallback((newChords: Chord[], key: string | null, keyIsMinor: boolean, cursor: number, barlineState: number[]) => {
        setHistory(prev => {
            const currentIndex = historyIndex === -1 ? -1 : historyIndex;
            const newHistory = prev.slice(0, currentIndex + 1);
            newHistory.push({ chords: [...newChords], key, keyIsMinor, cursor, barlines: [...barlineState] });
            setHistoryIndex(newHistory.length - 1);
            return newHistory;
        });
    }, [historyIndex]);

    const transposeNote = useCallback((note: string, delta: number): string => {
        if (!note) return note;
        const normalized = note.trim();
        const currentIndex = NOTES.findIndex(n => n.toUpperCase() === normalized.toUpperCase());
        if (currentIndex === -1) return note;
        const newIndex = (currentIndex + delta + NOTES.length) % NOTES.length;
        return NOTES[newIndex];
    }, []);

    const handleTranspose = useCallback((delta: number) => {
        if (chords.length === 0 && !keySignature) return;

        const transposedChords = chords.map(chord => ({
            ...chord,
            note: transposeNote(chord.note, delta)
        }));
        const transposedKey = keySignature ? transposeNote(keySignature, delta) : null;

        setChords(transposedChords);
        setKeySignature(transposedKey);
        addToHistory(transposedChords, transposedKey, keyMode === 'minor', cursorIndex, barlines);
    }, [addToHistory, barlines, chords, cursorIndex, keySignature, keyMode, transposeNote]);

    const playChordSound = useCallback((note: string, quality: 'major' | 'minor') => {
        try {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current.currentTime = 0;
                currentAudioRef.current = null;
            }

            const fileName = quality === 'minor' ? `${note}m.mp3` : `${note}.mp3`;
            const audioPath = `${process.env.PUBLIC_URL || ''}/chords/${fileName}`;

            const audio = new Audio(audioPath);

            audio.addEventListener('ended', () => {
                currentAudioRef.current = null;
            });

            audio.addEventListener('error', (e) => {
                console.error('Chord audio error:', e);
                currentAudioRef.current = null;
            });

            audio.play().catch((error) => {
                console.error('Chord audio play failed:', error);
                currentAudioRef.current = null;
            });

            currentAudioRef.current = audio;
        } catch (error) {
            console.error('Error playing chord sound:', error);
        }
    }, []);

    const handleAddChord = (note: string) => {
        const currentSpan = (() => {
            const relevantBarlines = barlines.filter(pos => pos <= cursorIndex).sort((a, b) => a - b);
            const spanStart = relevantBarlines.length > 0 ? relevantBarlines[relevantBarlines.length - 1] : 0;
            return cursorIndex - spanStart;
        })();

        if (currentSpan >= 4) {
            return;
        }

        const newChord: Chord = {
            note,
            quality: mode
        };

        playChordSound(note, mode);

        let newChords: Chord[];
        let nextCursor: number;

        if (cursorIndex === lastSelectedIndex) {
            newChords = [...chords];
            newChords[cursorIndex] = newChord;
            nextCursor = cursorIndex + 1;
            setLastSelectedIndex(null);
        } else {
            newChords = [
                ...chords.slice(0, cursorIndex),
                newChord,
                ...chords.slice(cursorIndex)
            ];
            nextCursor = Math.min(cursorIndex + 1, newChords.length);
        }

        setChords(newChords);
        setCursorIndex(nextCursor);
        const updatedBarlines = shiftBarlinesAfterInsert(barlines, cursorIndex, newChords.length);
        setBarlines(updatedBarlines);
        addToHistory(newChords, keySignature, keyMode === 'minor', nextCursor, updatedBarlines);
    };

    const handleRemoveChord = (index: number) => {
        const newChords = chords.filter((_, i) => i !== index);
        const nextCursor = Math.min(cursorIndex > index ? cursorIndex - 1 : cursorIndex, newChords.length);
        const updatedBarlines = shiftBarlinesAfterRemoval(barlines, index, newChords.length);
        setChords(newChords);
        setCursorIndex(nextCursor);
        setBarlines(updatedBarlines);
        if (lastSelectedIndex !== null) {
            if (index === lastSelectedIndex) {
                setLastSelectedIndex(null);
            } else if (index < lastSelectedIndex) {
                setLastSelectedIndex(lastSelectedIndex - 1);
            }
        }
        addToHistory(newChords, keySignature, keyMode === 'minor', nextCursor, updatedBarlines);
    };

    const handleMoveLeft = () => {
        if (cursorIndex > 0) {
            setCursorIndex(cursorIndex - 1);
        }
    };

    const handleMoveRight = () => {
        if (cursorIndex < chords.length) {
            setCursorIndex(cursorIndex + 1);
        }
    };

    const handleAddBarline = () => {
        if (cursorIndex <= 0 || cursorIndex > chords.length) return;
        if (barlines.includes(cursorIndex)) return;
        const nextBarlines = [...barlines, cursorIndex].sort((a, b) => a - b);
        setBarlines(nextBarlines);
        addToHistory(chords, keySignature, keyMode === 'minor', cursorIndex, nextBarlines);
    };

    const handleGenerate = () => {
        const generatedChords: Chord[] = [];
        const numChords = 4 + Math.floor(Math.random() * 4);

        for (let i = 0; i < numChords; i++) {
            const randomNote = NOTES[Math.floor(Math.random() * NOTES.length)];
            generatedChords.push({
                note: randomNote,
                quality: Math.random() > 0.5 ? 'major' : 'minor'
            });
        }

        setChords(generatedChords);
        const generatedCursor = generatedChords.length;
        setCursorIndex(generatedCursor);
        setBarlines([]);
        addToHistory(generatedChords, keySignature, keyMode === 'minor', generatedCursor, []);
    };

    const handleReharmonize = () => {
        if (chords.length === 0) return;

        const reharmonized = chords.map(chord => {
            const randomNote = NOTES[Math.floor(Math.random() * NOTES.length)];
            return {
                ...chord,
                note: randomNote
            };
        });

        const clampedCursor = Math.min(cursorIndex, reharmonized.length);
        setChords(reharmonized);
        setCursorIndex(clampedCursor);
        addToHistory(reharmonized, keySignature, keyMode === 'minor', clampedCursor, barlines);
    };

    const handleKeyChange = (value: string) => {
        const normalized = value === '' ? null : value;
        if (normalized === keySignature) return;
        setKeySignature(normalized);
        addToHistory(chords, normalized, keyMode === 'minor', cursorIndex, barlines);
    };

    const handleKeyModeChange = (newMode: 'major' | 'minor') => {
        setKeyMode(newMode);
        addToHistory(chords, keySignature, newMode === 'minor', cursorIndex, barlines);
    };

    const handleSelectChord = (index: number) => {
        setCursorIndex(index);
        setLastSelectedIndex(index);
        const chord = chords[index];
        if (chord) {
            playChordSound(chord.note, chord.quality);
        }
    };

    const handleDone = () => {
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current.currentTime = 0;
            currentAudioRef.current = null;
        }
        const formatted = formatChords(chords, barlines, keySignature, keyMode === 'minor');
        onSave(formatted);
        onClose();
    };

    React.useEffect(() => {
        if (!isOpen && currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current.currentTime = 0;
            currentAudioRef.current = null;
        }
    }, [isOpen]);

    React.useEffect(() => {
        return () => {
            if (currentAudioRef.current) {
                currentAudioRef.current.pause();
                currentAudioRef.current.currentTime = 0;
                currentAudioRef.current.src = '';
                currentAudioRef.current = null;
            }
        };
    }, []);

    if (!isOpen) return null;

    const canMoveLeft = cursorIndex > 0;
    const canMoveRight = cursorIndex < chords.length;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={ onClose }
        >
            <div
                className="bg-card rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                onClick={ (e) => e.stopPropagation() }
            >
                <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-xl font-semibold text-card-foreground">Build progression</h2>
                        <button
                            onClick={ onClose }
                            className="text-muted-foreground hover:text-card-foreground"
                        >
                            <X size={ 24 } />
                        </button>
                    </div>

                    <div className="flex items-center gap-2 mb-4">
                        <button
                            onClick={ () => handleTranspose(-1) }
                            disabled={ chords.length === 0 && !keySignature }
                            className={ `px-3 py-1.5 rounded border text-sm flex items-center gap-1 ${(chords.length === 0 && !keySignature)
                                ? 'border-muted text-muted-foreground cursor-not-allowed opacity-50'
                                : 'border-border text-card-foreground hover:bg-accent'
                                }` }
                        >
                            <ChevronDown size={ 16 } />
                            -1
                        </button>
                        <button
                            onClick={ () => handleTranspose(1) }
                            disabled={ chords.length === 0 && !keySignature }
                            className={ `px-3 py-1.5 rounded border text-sm flex items-center gap-1 ${(chords.length === 0 && !keySignature)
                                ? 'border-muted text-muted-foreground cursor-not-allowed opacity-50'
                                : 'border-border text-card-foreground hover:bg-accent'
                                }` }
                        >
                            <ChevronUp size={ 16 } />
                            +1
                        </button>
                        <button
                            disabled
                            className="px-3 py-1.5 rounded border border-muted text-sm flex items-center gap-1 text-muted-foreground cursor-not-allowed opacity-50"
                            title="Temporarily unavailable"
                        >
                            <RefreshCw size={ 16 } />
                            Generate
                        </button>
                        <button
                            disabled
                            className="px-3 py-1.5 rounded border border-muted text-sm flex items-center gap-1 text-muted-foreground cursor-not-allowed opacity-50"
                            title="Temporarily unavailable"
                        >
                            <RotateCw size={ 16 } />
                            Re-harmonize
                        </button>
                    </div>

                    <div className="mb-4">
                        <div className="min-h-[60px] border border-border rounded-md p-3 bg-background flex flex-wrap items-center gap-2">
                            { !keySignature && chords.length === 0 ? (
                                <div className="flex items-center gap-2 text-muted-foreground w-full">
                                    <ChevronDown size={ 16 } />
                                    <span>Select chords to build your progression.</span>
                                </div>
                            ) : (
                                <>
                                    { keySignature && (
                                        <div className="flex items-center gap-1 px-2 py-1 bg-accent rounded border border-border">
                                            <span className="text-sm text-card-foreground">{ `${keySignature}${keyMode === 'minor' ? 'm' : ''}:` }</span>
                                        </div>
                                    ) }
                                    { chords.map((chord, index) => (
                                        <React.Fragment key={ `${chord.note}-${index}` }>
                                            { cursorIndex === index && (
                                                <div className="flex items-center">
                                                    <div className="w-1 h-6 bg-blue-500 rounded-full animate-pulse mx-1" />
                                                </div>
                                            ) }
                                            <div
                                                className={ `flex items-center gap-1 px-2 py-1 rounded border border-border cursor-pointer transition ${lastSelectedIndex === index
                                                    ? 'bg-blue-200 dark:bg-blue-900/40'
                                                    : 'bg-accent'
                                                    }` }
                                                onClick={ () => handleSelectChord(index) }
                                            >
                                                <span className="text-sm text-card-foreground">
                                                    { chord.note }{ chord.quality === 'minor' ? 'm' : '' }
                                                </span>
                                                <button
                                                    onClick={ (e) => {
                                                        e.stopPropagation();
                                                        handleRemoveChord(index);
                                                    } }
                                                    className="text-muted-foreground hover:text-card-foreground ml-1"
                                                >
                                                    <X size={ 14 } />
                                                </button>
                                            </div>
                                            { barlines.includes(index + 1) && (
                                                <span className="text-sm text-muted-foreground font-semibold px-1">|</span>
                                            ) }
                                        </React.Fragment>
                                    )) }
                                    { cursorIndex === chords.length && (
                                        <div className="flex items-center">
                                            <div className="w-1 h-6 bg-blue-500 rounded-full animate-pulse mx-1" />
                                        </div>
                                    ) }
                                </>
                            ) }
                        </div>
                    </div>

                    { chords.length > 0 && (
                        <div className="mb-4">
                            <div className="flex items-center gap-4 mb-2">
                                <button
                                    onClick={ handleMoveLeft }
                                    disabled={ !canMoveLeft }
                                    className={ `flex items-center gap-1 text-sm ${!canMoveLeft
                                        ? 'text-muted-foreground cursor-not-allowed'
                                        : 'text-card-foreground hover:underline'
                                        }` }
                                >
                                    <ChevronLeft size={ 16 } />
                                    Move Left
                                </button>
                                <span className="text-sm text-card-foreground">
                                    Cursor position: { cursorIndex + 1 }/{ chords.length + 1 }
                                </span>
                                <button
                                    onClick={ handleMoveRight }
                                    disabled={ !canMoveRight }
                                    className={ `flex items-center gap-1 text-sm ${!canMoveRight
                                        ? 'text-muted-foreground cursor-not-allowed'
                                        : 'text-card-foreground hover:underline'
                                        }` }
                                >
                                    Move Right
                                    <ChevronRight size={ 16 } />
                                </button>
                            </div>
                            <div className="text-sm text-blue-500">
                                Click note buttons to insert a chord at the cursor.
                                After 4 chords in a span, add a barline before inserting more.
                            </div>
                        </div>
                    ) }

                    { chords.length === 0 && (
                        <div className="mb-4 p-3 bg-green-100 dark:bg-green-900/20 rounded-md border border-green-200 dark:border-green-800">
                            <p className="text-sm text-green-800 dark:text-green-200">
                                Select a key to get started - click any chord button below.
                            </p>
                        </div>
                    ) }

                    <div className="mb-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div className="flex items-center gap-2">
                                <label className="text-sm font-medium text-card-foreground">Key signature</label>
                                <div className="flex items-center gap-2">
                                    <select
                                        value={ keySignature ?? '' }
                                        onChange={ (e) => handleKeyChange(e.target.value) }
                                        className="px-3 py-1.5 border border-border rounded-md bg-background text-card-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                                    >
                                        <option value="">No key</option>
                                        { NOTES.map((note) => (
                                            <option key={ note } value={ note }>{ note }{ keyMode === 'minor' ? 'm' : '' }</option>
                                        )) }
                                    </select>
                                    <div className="flex gap-1 border border-border rounded-md overflow-hidden">
                                        <button
                                            onClick={ () => handleKeyModeChange('major') }
                                            className={ `px-2 py-1.5 text-xs font-medium transition ${keyMode === 'major'
                                                ? 'bg-card-foreground text-card'
                                                : 'bg-background text-card-foreground hover:bg-accent'
                                                }` }
                                        >
                                            Major
                                        </button>
                                        <button
                                            onClick={ () => handleKeyModeChange('minor') }
                                            className={ `px-2 py-1.5 text-xs font-medium transition ${keyMode === 'minor'
                                                ? 'bg-card-foreground text-card'
                                                : 'bg-background text-card-foreground hover:bg-accent'
                                                }` }
                                        >
                                            Minor
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <button
                                onClick={ handleAddBarline }
                                className="px-3 py-1.5 rounded border border-border text-sm flex items-center gap-1 text-card-foreground hover:bg-accent"
                            >
                                <Code size={ 16 } />
                                Add Barline
                            </button>
                        </div>
                    </div>

                    <div className="mb-6">
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={ () => setMode('major') }
                                className={ `flex-1 py-2 rounded border text-sm font-medium ${mode === 'major'
                                    ? 'bg-card-foreground text-card border-border'
                                    : 'bg-background text-card-foreground border-border hover:bg-accent'
                                    }` }
                            >
                                Major
                            </button>
                            <button
                                onClick={ () => setMode('minor') }
                                className={ `flex-1 py-2 rounded border text-sm font-medium ${mode === 'minor'
                                    ? 'bg-card-foreground text-card border-border'
                                    : 'bg-background text-card-foreground border-border hover:bg-accent'
                                    }` }
                            >
                                Minor
                            </button>
                        </div>

                        <div className="grid grid-cols-4 gap-2">
                            { NOTES.map((note) => (
                                <button
                                    key={ note }
                                    onClick={ () => handleAddChord(note) }
                                    className="py-2 px-3 rounded border text-sm font-medium bg-background text-card-foreground border-border hover:bg-accent"
                                >
                                    { note }
                                </button>
                            )) }
                        </div>
                    </div>

                    <button
                        onClick={ handleDone }
                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition"
                    >
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
};

